import {
  addRoomItem,
  DEFAULT_WALL_HEIGHT,
  DEFAULT_WALL_THICKNESS,
  emptyRoomPlan,
  freeItemCentre,
  itemSpan,
  removeRoomLine,
  ROOM_ITEM_DEFAULTS,
  ROOM_ITEM_LABEL,
  setCornerHeight,
  type ProjectDocument,
  type RoomItemKind,
  type RoomPlan,
} from '@scs/domain-engine';
import { MmInput } from '../lib/ui';
import { ItemProps, LineProps } from './RoomEditor';
import { cornerLabel, itemLabel, lineLabel } from './roomLabels';
import type { EditorStore, Selection } from './store';

type Exec = EditorStore['exec'];
export type RoomSel = NonNullable<Selection['room']>;

export const ROOM_ITEM_KINDS: RoomItemKind[] = ['window', 'door', 'socket', 'drain', 'water'];
export const ROOM_ITEM_ICON: Record<RoomItemKind, string> = { window: '▢', door: '⊓', socket: '⏻', drain: '◎', water: '💧' };

/** Edits the room plan as one undo step (the plan exists from here on). */
export function roomEdit(exec: Exec, label: string, fn: (p: RoomPlan) => void) {
  exec(label, (d) => {
    d.roomPlan ??= emptyRoomPlan();
    fn(d.roomPlan);
  });
}

/** Adds a window / door / socket … to a wall, in its widest free stretch; returns the new item id. */
export function addItemToWall(exec: Exec, kind: RoomItemKind, lineId: string): string | null {
  let id: string | null = null;
  roomEdit(exec, `Novi element: ${ROOM_ITEM_LABEL[kind]}`, (p) => {
    id = addRoomItem(p, kind, lineId, freeItemCentre(p, lineId, ROOM_ITEM_DEFAULTS[kind].width))?.id ?? null;
  });
  return id;
}

export function removeRoomSelection(exec: Exec, plan: RoomPlan, sel: RoomSel) {
  // A corner is not deleted on its own (delete a wall instead).
  if (sel.pointId) return;
  if (sel.itemId) {
    const it = plan.items.find((i) => i.id === sel.itemId);
    roomEdit(exec, `Brisanje: ${it ? itemLabel(plan, it) : 'element'}`, (p) => (p.items = p.items.filter((i) => i.id !== sel.itemId)));
  } else roomEdit(exec, `Brisanje: ${lineLabel(plan, sel.lineId)}`, (p) => removeRoomLine(p, sel.lineId));
}

/** Properties of the selected wall (length, angles, corner heights, items on it) or of an item in it. */
export function RoomProperties({ doc, sel, exec, disabled, onSelect }: { doc: ProjectDocument; sel: RoomSel; exec: Exec; disabled: boolean; onSelect(sel: Selection['room']): void }) {
  const plan = doc.roomPlan;
  const line = plan?.lines.find((l) => l.id === sel.lineId);
  if (!plan || !line) return null;
  const edit = (label: string, fn: (p: RoomPlan) => void) => roomEdit(exec, label, fn);
  if (sel.pointId) return <CornerProperties plan={plan} pointId={sel.pointId} exec={exec} disabled={disabled} onSelect={onSelect} />;
  const item = sel.itemId ? plan.items.find((i) => i.id === sel.itemId) : undefined;
  if (item)
    return (
      <>
        <ItemProps plan={plan} itemId={item.id} readOnly={disabled} edit={edit} onRemove={() => (removeRoomSelection(exec, plan, sel), onSelect({ lineId: line.id, itemId: null }))} />
        <div className="prop-group">
          <button className="btn sm ghost" onClick={() => onSelect({ lineId: line.id, itemId: null })}>
            ← {lineLabel(plan, line.id)}
          </button>
        </div>
      </>
    );
  const items = plan.items.filter((i) => i.lineId === line.id).sort((a, b) => (itemSpan(plan, a)?.u0 ?? 0) - (itemSpan(plan, b)?.u0 ?? 0));
  const wallH = plan.wallHeight ?? DEFAULT_WALL_HEIGHT;
  const corner = (pid: string, label: string) => {
    const pt = plan.points.find((p) => p.id === pid);
    if (!pt) return null;
    return (
      <div className="prop-row">
        <label title="Visina vertikalne ivice zida; različite visine prave kosinu">
          <button className="btn sm ghost link-btn" onClick={() => onSelect({ lineId: line.id, pointId: pid })} title="Izaberi ovaj ugao">
            {label} ({cornerLabel(plan, pid)})
          </button>
        </label>
        <MmInput label={`Visina: ${label}`} value={pt.h ?? wallH} min={1} max={20000} disabled={disabled} onCommit={(v) => edit('Visina ugla (kosina)', (p) => setCornerHeight(p, pid, v))} />
      </div>
    );
  };
  const sloped = [line.a, line.b].some((pid) => plan.points.find((p) => p.id === pid)?.h !== undefined);
  return (
    <>
      <LineProps plan={plan} lineId={line.id} readOnly={disabled} edit={edit} onRemove={() => (removeRoomSelection(exec, plan, sel), onSelect(null))} />
      <div className="prop-group">
        <h3>Visina zida</h3>
        {corner(line.a, 'Početak')}
        {corner(line.b, 'Kraj')}
        {sloped && (
          <button className="btn sm" disabled={disabled} onClick={() => edit('Visina uglova kao zid', (p) => (setCornerHeight(p, line.a, null), setCornerHeight(p, line.b, null)))}>
            ↺ Bez kosine
          </button>
        )}
        <p className="faint" style={{ fontSize: 11 }}>Uglove dijeli sa susjednim zidom — njegova ivica se mijenja zajedno.</p>
      </div>
      <div className="prop-group">
        <h3>Na zidu ({items.length})</h3>
        {items.map((i) => (
          <button key={i.id} className="btn sm ghost room-item-row" onClick={() => onSelect({ lineId: line.id, itemId: i.id })}>
            <span>{ROOM_ITEM_ICON[i.kind]}</span> {itemLabel(plan, i)}
            <span className="faint mono">
              {Math.round(i.width / 1000)}×{Math.round(i.height / 1000)}
            </span>
          </button>
        ))}
        <div className="room-add-row">
          {ROOM_ITEM_KINDS.map((k) => (
            <button
              key={k}
              className="btn sm"
              disabled={disabled}
              title={`Dodaj: ${ROOM_ITEM_LABEL[k]} (na najslobodnije mjesto na zidu)`}
              onClick={() => {
                const id = addItemToWall(exec, k, line.id);
                if (id) onSelect({ lineId: line.id, itemId: id });
              }}
            >
              + {ROOM_ITEM_LABEL[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="prop-group">
        <h3>Svi zidovi</h3>
        <label className="opt-row" title="Plafon kroz gornje uglove zidova (kosine ga naginju); zidovi su odsječeni njegovom ravni">
          <input type="checkbox" checked={!!plan.showCeiling} disabled={disabled} onChange={(e) => edit(e.target.checked ? 'Prikaži plafon' : 'Sakrij plafon', (p) => (e.target.checked ? (p.showCeiling = true) : delete p.showCeiling))} /> Prikaži plafon
        </label>
        <div className="prop-row">
          <label>Visina</label>
          <MmInput label="Visina zidova" value={wallH} min={100} max={20000} disabled={disabled} onCommit={(v) => edit('Visina zidova', (p) => (p.wallHeight = v))} />
        </div>
        <div className="prop-row">
          <label>Debljina</label>
          <MmInput label="Debljina zidova" value={plan.wallThickness ?? DEFAULT_WALL_THICKNESS} min={1} max={2000} disabled={disabled} onCommit={(v) => edit('Debljina zidova', (p) => (p.wallThickness = v))} />
        </div>
      </div>
    </>
  );
}

/** A wall corner (vertical edge): its height; the walls meeting there slope to their other ends. */
function CornerProperties({ plan, pointId, exec, disabled, onSelect }: { plan: RoomPlan; pointId: string; exec: Exec; disabled: boolean; onSelect(sel: Selection['room']): void }) {
  const pt = plan.points.find((p) => p.id === pointId);
  if (!pt) return null;
  const wallH = plan.wallHeight ?? DEFAULT_WALL_HEIGHT;
  const walls = plan.lines.filter((l) => l.kind === 'wall' && (l.a === pointId || l.b === pointId));
  const edit = (label: string, fn: (p: RoomPlan) => void) => roomEdit(exec, label, fn);
  return (
    <div className="prop-group">
      <h3>{cornerLabel(plan, pointId)}</h3>
      <div className="prop-row">
        <label title="Visina vertikalne ivice zida u ovom uglu">Visina</label>
        <MmInput label="Visina ugla" value={pt.h ?? wallH} min={1} max={20000} disabled={disabled} onCommit={(v) => edit(`Visina: ${cornerLabel(plan, pointId)}`, (p) => setCornerHeight(p, pointId, v))} />
      </div>
      {pt.h !== undefined && (
        <button className="btn sm" disabled={disabled} onClick={() => edit(`Visina kao zid: ${cornerLabel(plan, pointId)}`, (p) => setCornerHeight(p, pointId, null))}>
          ↺ Kao visina zida ({Math.round(wallH / 1000)})
        </button>
      )}
      <p className="faint" style={{ fontSize: 11 }}>Gornja ivica svakog zida ide pravo od ovog ugla do drugog kraja zida — tako nastaju kosine. Ostali uglovi zadržavaju svoju visinu.</p>
      <h3 style={{ marginTop: 8 }}>Zidovi u uglu</h3>
      {walls.map((w) => (
        <button key={w.id} className="btn sm ghost room-item-row" onClick={() => onSelect({ lineId: w.id })}>
          ▮ {lineLabel(plan, w.id)}
        </button>
      ))}
    </div>
  );
}
